import { CLIPS, frameAt, frameKey, resolveClip } from '../buffy/clips';
import { LINKS, STEPS } from '../storyScript';

/* buffyTrack: the hero's ONE timeline (motion spec 3.4). Buffy's clips, laid
   end to end with no gaps, ARE the master clock. Every UI beat in the scene is
   written as cue('<segment id>.<contact>', offset), so when a clip's timing
   changes in its animation.json, the whole story re-times itself.

   A segment: { id, clip, start, dur, end, pos, pose, mirror }.
     clip    a registry name (buffy/clips.js); a missing clip plays REST for
             its planned duration (CLIP_FALLBACK)
     pos     always 'HOME': the owner (2026-09-29) wants Buffy beside the
             composer for the whole loop, at every size ("I don't want Buffy
             to jump up above"); no jumps, rises or level changes
     pose    for `look` holds: 'LEFT' | 'RIGHT' | 'UP' | 'DOWN'
   Two variants: the first run opens with WAVE; later runs open with IDLE
   (whose breathing phase is global, so the loop seam is seamless).

   The adjacency rule, the seam, the airborne-only moves and the cue names are
   proven by buffyTrack.test.js, not eyeballed. */

export const STEP_IDS = STEPS.map((st) => st.id);
const mk = (i) => `step:${STEP_IDS[i]}`;

/* Pace (owner, 2026-09-29): the teacher types 55 ms a character, Buffy 28;
   the chat eases between the middle of the stage and the dock in 600 ms. */
export const TEACHER_MS = 55;
export const BUFFY_MS = 28;
export const CHAT_EASE = 600;
/* Every step's key moment (the connected map, the lowest row, the "why",
   the Assigned draft, the gains) stays on screen, settled, at least this
   long before the stage clears (design review 2026-09-29, section c). The
   scene clears OUT ms before the next step starts; a pop settles in POP ms
   (hero/tween.js DUR.pop). */
export const KEY_READ = 2500;
export const OUT = 150;
const POP = 280;
/* Step 1 shows every link (10 of 11 chapters, owner): the first plugs on
   his connect gesture, the rest draw in a quick cascade LINK_GAP apart, each
   line LINK_DRAW long (hero/sceneTimeline.js). */
export const LINK_GAP = 120;
export const LINK_DRAW = 260;
const chars = (text) => Array.from(text || '').length;

export function buildTrack({ firstRun = true, registry = CLIPS } = {}) {
  const segs = [];
  const marks = {};
  let t = 0;
  let pos = 'HOME';

  const add = (clipName, opts = {}) => {
    const clip = resolveClip(clipName, registry);
    const dur = opts.dur ?? clip.effDur;
    if (!(dur > 0)) throw new Error(`buffyTrack: "${clipName}" needs an explicit dur`);
    const seg = {
      i: segs.length,
      id: opts.id || null,
      clip: clipName,
      start: t,
      dur,
      end: t + dur,
      pose: opts.pose || null,
      mirror: !!opts.mirror,
      pos: opts.move ? { from: pos, to: opts.move } : pos,
      arc: opts.arc || null,
      globalPhase: clipName === 'idle-breathe',
    };
    segs.push(seg);
    if (opts.move) pos = opts.move;
    t += dur;
    return seg;
  };
  const mark = (name, at = t) => { marks[name] = at; };
  // a contact's offset into its (trimmed) segment
  const off = (clipName, contact) => {
    const c = resolveClip(clipName, registry);
    return (c.contacts[contact] ?? 0) - c.trimIn;
  };
  // a listen hold ends on its frame 1 (next to rest; never on frame 2, and
  // not on frame 0, which would merge with the rest beats around it into a
  // long still)
  const listenDur = (d) => {
    const m = d % 1550;
    if (m === 0) return d - 100;
    if (m > 1450) return d - (m - 1450);
    if (m <= 150) return d + (200 - m);
    return (m > 550 && m <= 650) || (m > 1000 && m <= 1100) ? d + 100 : d;
  };
  // the legal listen length nearest to d (shorter or longer), never under
  // minD: for the listen before an answer, so the frame rule nudges the
  // answer by a few ms instead of always pushing the story later
  const listenNear = (d, minD) => {
    for (let x = d; x >= minD; x -= 10) if (listenDur(x) === x) return x;
    return listenDur(d);
  };
  // hold until time T (at least 200 ms, so no plain rest ever runs long);
  // any gesture may follow. Returns when the fill ends.
  const fillTo = (T) => {
    if (T - t <= 0) return t;
    const end = Math.max(T, t + 300);
    add('rest', { dur: 100 });
    // holds listen (ears, blinks): the cycle starts at the hold, so it always
    // ends on frame 1 (idle breathes on the global clock and could end on its
    // rest cell, merging with the rest beats into a long still)
    add('listen', { dur: listenDur(end - t) });
    return t;
  };
  /* Beats A (ask) and B (answer) of a step, from its start `S` (the chat
     eases up over the first 700 ms; step 1 starts there already). Returns
     the moment the chat eases down (beat C). */
  const askAndAnswer = (i, { typeAt, answerClip, answerId, listenId, answerLead = 350 }) => {
    const step = STEPS[i - 1];
    const typeStart = typeAt;
    const send = typeStart + chars(step.teacher) * TEACHER_MS + 250;
    mark(`beat:${i}:type`, typeStart);
    mark(`beat:${i}:send`, send);
    // he answers about answerLead after the send (never before send + 150)
    const answerStart = send + answerLead;
    add('listen', { id: listenId, dur: listenNear(answerStart - t, send + 150 - t) });
    const reply = answerClip === 'catch' ? t + off('catch', 'catch') + 100 : send + 450;
    mark(`beat:${i}:reply`, reply);
    if (answerClip === 'look') {
      // a thoughtful look up while he answers (no head-tilt art)
      add('rest', { dur: 100 });
      add('look', { id: answerId, pose: 'UP', dur: 350 });
      add('rest', { dur: 100 });
    } else add(answerClip, { id: answerId });
    const down = fillTo(Math.max(reply + chars(step.buffy) * BUFFY_MS + 900, t + 100));
    mark(`beat:${i}:down`, down);
    return down;
  };

  /* Hand-offs (design QA 2026-09-29): gestures meet through ONE 100 ms rest
     beat (their rest tails are trimmed, see buffy/clips.js TRIMS); a look
     pose is always entered from rest; no rest runs longer than 200 ms, and
     longer quiet moments breathe or listen instead of freezing.

     Pace (owner, 2026-09-29: "a little slower through the whole thing so it
     is easier to understand"): about 1.35x the earlier loop. The teacher
     types at 55 ms a character, Buffy's lines at 28; each step's key moment
     (the course read, the connected map, the three cards, the Assigned
     draft, the question with Why B) holds at least 2.5 s while he breathes,
     listens or looks. A `listen` hold must end on its frame 0 or 1 (both
     sit next to rest): 1550 = one cycle, 2000 and 3800 end on frame 1. */

  /* A key-moment hold: he listens until the step's key moment (keyAt, ms)
     has been on screen, settled, for KEY_READ before the stage clears, and
     for at least `min` ms. */
  const holdKey = (id, keyAt, min) => {
    add('listen', { id, dur: listenDur(Math.max(min, keyAt + KEY_READ + OUT - t)) });
  };

  /* Re-timing (design review 2026-09-29, section c): dead air and the long
     course read are cut; no typing, clip or ease is faster. Each step types
     550 ms after it starts (the chat rises from the step's start, once the
     last step's cards are gone, and has settled in the middle by then). */

  /* ---- Step 1: Choose your curriculum ----
     A: the file card glides into the composer (drop at 1250) and the line
     types; B: he catches the file from the sent message as he answers; C:
     the chat docks, then (never before: no sliding with an arm out) he
     points at the course as he reads it, the course slides over for the
     SAT skills window, he plugs chapter 1 into its skill and the other nine
     links follow in a quick cascade (all 10 of 11, owner 2026-09-30). */
  mark(mk(0));
  if (firstRun) add('wave', { id: 'hello' });
  else add('idle-breathe', { id: 'hello', dur: 1400 }); // every idle cell sits next to rest
  add('rest', { dur: 100 });
  add('look', { id: 'watchFile', pose: 'RIGHT', dur: 350 });
  add('rest', { dur: 100 });
  let down = askAndAnswer(1, { typeAt: 1650, answerClip: 'catch', answerId: 'catch', listenId: 'ask1', answerLead: 300 });
  fillTo(down + CHAT_EASE + 50);
  add('point', { id: 'present' });
  // the course reads its chapters from present.hold (110 ms a row); as he
  // lowers his arm it slides over and the SAT skills window arrives
  mark('beat:1:split', t - 150);
  add('rest', { dur: 100 });
  // the connect sheet as drawn: he GRABS the cable on his right (screen
  // left, frames 1-2) and PLUGS it toward the windows (screen right, frames
  // 4-6, the plug contact), so it plays unmirrored (checked frame by frame
  // 2026-09-30: mirroring it made the plug reach away from the windows)
  const c1 = add('connect', { id: 'connect1' });
  // his plug connects chapter 1; the other links (all 10, owner) draw in a
  // quick cascade LINK_GAP apart while he plugs once more, then he cheers
  // as the last chip lands
  const lastPlug = c1.start + off('connect', 'plug') + LINK_GAP * (LINKS.length - 1);
  mark('beat:1:linked', lastPlug);
  add('rest', { dur: 100 });
  add('connect-quick', { id: 'connect2' });
  add('rest', { dur: 100 });
  add('celebrate', { id: 'bridgeCheer' });
  add('rest', { dur: 100 });
  add('look', { id: 'admire', pose: 'RIGHT', dur: 350 });
  add('rest', { dur: 100 });
  holdKey('mapHold', lastPlug + POP, 700);

  /* ---- Step 2: See where they struggle ---- */
  mark(mk(1));
  add('rest', { dur: 100 });
  down = askAndAnswer(2, { typeAt: t + 450, answerClip: 'point', answerId: 'answer2', listenId: 'ask2' });
  // the results card is on stage from down + 300 and its bars are full by
  // down + 1460; he looks at it once the chat has settled, then points out
  // the lowest row
  fillTo(down + CHAT_EASE + 50);
  add('rest', { dur: 100 });
  add('look', { id: 'lookResults', pose: 'RIGHT', dur: 350 });
  const lowShown = t + off('point', 'tap') + POP;
  add('point', { id: 'pointWeak' });
  add('rest', { dur: 100 });
  holdKey('resultsHold', lowShown, 1700);

  /* ---- Step 3: See why ---- */
  mark(mk(2));
  add('rest', { dur: 100 });
  down = askAndAnswer(3, { typeAt: t + 450, answerClip: 'think', answerId: 'thinkWhy', listenId: 'ask3' });
  fillTo(down + 500);
  add('rest', { dur: 100 });
  add('look', { id: 'lookQuestion', pose: 'UP', dur: 400 });
  const whyShown = t + off('point', 'tap') + POP;
  add('point', { id: 'pointB' });
  add('rest', { dur: 100 });
  holdKey('whyHold', whyShown, 2400);

  /* ---- Step 4: Practice ---- */
  mark(mk(3));
  add('rest', { dur: 100 });
  down = askAndAnswer(4, { typeAt: t + 450, answerClip: 'encourage', answerId: 'answerDraft', listenId: 'listenAssign' });
  // the draft is on stage from down + 300; the teacher reads it, then clicks
  // (lookAssign.start + 100); the adaptive-practice card is settled 740 ms
  // after the click
  fillTo(down + 2100);
  add('rest', { dur: 100 });
  const assigned = t + 100 + 740;
  add('look', { id: 'lookAssign', pose: 'RIGHT', dur: 350 });
  add('celebrate', { id: 'assignCheer' });
  add('rest', { dur: 100 });
  add('look', { id: 'assignHold', pose: 'RIGHT', dur: 300 });
  add('rest', { dur: 100 });
  holdKey('assignedHold', assigned, 2000);

  /* ---- Step 5: Improve over time ---- */
  mark(mk(4));
  add('rest', { dur: 100 });
  down = askAndAnswer(5, { typeAt: t + 450, answerClip: 'encourage', answerId: 'answer5', listenId: 'ask5' });
  fillTo(down + 700);
  add('rest', { dur: 100 });
  // the gains grow from lookProgress.start + 100 (120 ms apart, 520 ms each)
  const gainsShown = t + 100 + 240 + 520;
  add('look', { id: 'lookProgress', pose: 'RIGHT', dur: 350 });
  add('celebrate', { id: 'progressCheer' });
  add('rest', { dur: 100 });
  // the scene clears (CHAT_EASE + OUT + 300 before the end) and the chat
  // eases back up for step 1 inside this hold
  add('idle-breathe', { id: 'idleClose', dur: Math.max(2500, gainsShown + KEY_READ + CHAT_EASE + OUT + 300 - t) });
  mark('step:end');

  const loopMs = t;
  const byId = Object.fromEntries(segs.filter((s) => s.id).map((s) => [s.id, s]));
  return { segs, marks, loopMs, byId, firstRun, registry };
}

/* ---------- reading the track ---------- */

export const clipOf = (track, seg) => resolveClip(seg.clip, track.registry);

/* ms of a named moment: 'segId.contact' | 'segId.start' | 'segId.end' |
   'step:<id>'. Throws on an unknown name (validator check 6). */
export function cueTime(track, name) {
  if (name.startsWith('step:') || name.startsWith('beat:')) {
    if (!(name in track.marks)) throw new Error(`cue: unknown mark "${name}"`);
    return track.marks[name];
  }
  const dot = name.indexOf('.');
  const segId = dot < 0 ? name : name.slice(0, dot);
  const key = dot < 0 ? 'start' : name.slice(dot + 1);
  const seg = track.byId[segId];
  if (!seg) throw new Error(`cue: unknown segment "${segId}" in "${name}"`);
  if (key === 'start') return seg.start;
  if (key === 'end') return seg.end;
  const clip = clipOf(track, seg);
  const c = clip.contacts[key];
  if (!Number.isFinite(c)) throw new Error(`cue: "${seg.clip}" has no contact "${key}" (${name})`);
  return seg.start + c - clip.trimIn;
}

/* Segment index at loop time t (binary search; the actor keeps a pointer). */
export function segIndexAt(track, t) {
  const { segs } = track;
  let lo = 0;
  let hi = segs.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (segs[mid].start <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/* Clip-local (untrimmed) ms for a segment at loop time t. `abs` is the
   absolute clock (runs * loop + t): IDLE breathes on it, so two IDLE
   segments that touch across the seam are one breath. */
export function localAt(track, seg, t, abs = t) {
  const clip = clipOf(track, seg);
  if (clip.playback === 'loop') {
    if (seg.globalPhase) return ((abs % clip.durationMs) + clip.durationMs) % clip.durationMs;
    return t - seg.start;
  }
  return clip.trimIn + (t - seg.start);
}

export function frameKeyAt(track, seg, t, abs = t) {
  const clip = clipOf(track, seg);
  return frameKey(clip, frameAt(clip, localAt(track, seg, t, abs), seg.pose));
}

/* Airborne window of a jump segment, in segment-local ms. */
export function airborneOf(track, seg) {
  const clip = clipOf(track, seg);
  const a = clip.airborne || [clip.trimIn, clip.trimIn];
  return [a[0] - clip.trimIn, a[1] - clip.trimIn];
}

/* Feet position (stage px) at t: his surface. Every segment is at HOME
   (no jumps); a { from, to } segment would still resolve to its landing
   surface, so nothing can ever move him mid-clip. */
export function posAt(track, seg, t, geo) {
  const at = typeof seg.pos === 'string' ? seg.pos : seg.pos.to;
  return { ...geo[at], air: 0 };
}

/* Which surface's shadow shows, and how strong (motion spec 2.2). */
export function shadowAt(track, seg, t, geo) {
  const p = posAt(track, seg, t, geo);
  return { surface: 'HOME', x: p.x, o: 1, sx: 1 };
}

/* The poster (reduced motion, ?step=<id>&settled=1): scene time + a still
   Buffy (motion spec 4.8, with the owner's no-props changes). */
export function postersOf(track) {
  const m = track.marks;
  // each step's poster is its beat C, just before the stage clears
  const at = (i) => (i + 1 < STEP_IDS.length ? m[mk(i + 1)] : m['step:end'] - 1500) - 400;
  const clips = ['point', 'point', 'point', 'celebrate', 'celebrate'];
  const locals = [1100, 1100, 1100, 1300, 1300];
  return Object.fromEntries(STEP_IDS.map((id, i) => [id, { t: at(i), buffy: { clip: clips[i], local: locals[i], pos: 'HOME' } }]));
}

/* Step windows for the progress row. */
export function stepsOf(track) {
  const m = track.marks;
  return STEP_IDS.map((id, i) => ({
    id,
    start: m[`step:${id}`],
    end: i + 1 < STEP_IDS.length ? m[`step:${STEP_IDS[i + 1]}`] : m['step:end'],
  }));
}

export function stepIndexAt(steps, t) {
  for (let i = steps.length - 1; i >= 0; i -= 1) if (t >= steps[i].start) return i;
  return 0;
}

/* Frames of a sheet that show a story object in Buffy's hooves (claims for
   validator check 7): the catch sheet draws the curriculum file in 1-8. */
export const SPRITE_CLAIMS = { catch: { curriculumFile: [1, 2, 3, 4, 5, 6, 7, 8] } };

export function spriteClaims(track) {
  const out = [];
  track.segs.forEach((seg) => {
    const clip = clipOf(track, seg);
    const table = SPRITE_CLAIMS[clip.sheet];
    if (!table || clip.playback !== 'once') return;
    Object.entries(table).forEach(([prop, frames]) => {
      let from = null;
      for (let ms = 0; ms <= seg.dur; ms += 10) {
        const f = frameAt(clip, clip.trimIn + ms);
        const on = frames.includes(f) && ms < seg.dur;
        if (on && from === null) from = seg.start + ms;
        if (!on && from !== null) {
          out.push({ prop, from, to: seg.start + ms, owner: `buffy:${seg.id || seg.clip}` });
          from = null;
        }
      }
    });
  });
  return out;
}
